import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button, Center, Stack, Text, Title } from "@mantine/core";
import { MdErrorOutline } from "react-icons/md";

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    isDomError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
    state: State = { hasError: false, isDomError: false };

    static getDerivedStateFromError(error: unknown): State {
        const isDomError = error instanceof Error && error.message.includes("insertBefore");
        return { hasError: true, isDomError };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error("ErrorBoundary caught:", error, info.componentStack);
    }

    render() {
        if (!this.state.hasError) return this.props.children;

        const hint = this.state.isDomError
            ? "This can happen when a browser extension (e.g. a translator or password manager) modifies the page. Try disabling extensions or reloading."
            : "An unexpected error occurred.";

        return (
            <Center h="100vh">
                <Stack align="center" gap="md" maw={480} ta="center" px="xl">
                    <MdErrorOutline size={40} color="var(--mantine-color-red-6)" />
                    <Title order={3}>Something went wrong</Title>
                    <Text c="dimmed" size="sm">
                        {hint}
                    </Text>
                    <Button onClick={() => window.location.reload()}>Reload page</Button>
                </Stack>
            </Center>
        );
    }
}
